import React, { useCallback, useRef, useState } from 'react';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import ImagePropertiesModal from './ImagePropertiesModal';
import { CustomStylesEditing, CustomStylesUI } from './ckeditorCustomStyles';
import './CKEditors.css';

const IMAGE_ICON =
    '<svg viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path d="M6.66 9.118a.693.693 0 0 1 .956.032l3.65 3.411 2.422-2.238a.695.695 0 0 1 .945 0L17.5 13.6V2.5h-15v11.1l4.16-4.482ZM17.8 1c.652 0 1.2.47 1.2 1.1v14.362c0 .64-.532 1.038-1.184 1.038H2.184C1.532 17.5 1 17.103 1 16.462V2.1C1 1.47 1.537 1 2.2 1h15.6Zm-5.655 6a2.128 2.128 0 0 1 .157-2.364A2.133 2.133 0 1 1 12.145 7Z"/></svg>';

const HTML_IMAGE_ATTRS = ['border', 'hspace', 'vspace', 'align'];

function toPx(value) {
    if (value === null || value === undefined || value === '') {
        return '';
    }
    return /^\d+(\.\d+)?$/.test(String(value)) ? `${value}px` : String(value);
}

function isBlockImage(image) {
    return image && image.is('element', 'imageBlock');
}

function alignToImageStyle(image, align) {
    const resolvedAlign = align || 'left';

    if (resolvedAlign === 'center') {
        return isBlockImage(image) ? 'alignCenter' : 'inline';
    }
    if (resolvedAlign === 'right') {
        return isBlockImage(image) ? 'alignBlockRight' : 'alignRight';
    }
    return isBlockImage(image) ? 'alignBlockLeft' : 'alignLeft';
}

function imageStyleToAlign(imageStyle) {
    switch (imageStyle) {
    case 'alignBlockLeft':
    case 'alignLeft':
    case 'inline':
        return 'left';
    case 'alignCenter':
    case 'block':
        return 'center';
    case 'alignBlockRight':
    case 'alignRight':
    case 'side':
        return 'right';
    default:
        return 'left';
    }
}

function applyImageAlignmentStyles(writer, img, align, hspace, vspace) {
    writer.removeStyle('float', img);
    writer.removeStyle('display', img);
    writer.removeStyle('margin', img);
    writer.removeStyle('margin-left', img);
    writer.removeStyle('margin-right', img);

    const horizontalSpace = hspace ? `${hspace}px` : '0';
    const verticalSpace = vspace ? `${vspace}px` : '0';

    if (align === 'left') {
        writer.setStyle('float', 'left', img);
        writer.setStyle('margin', `${verticalSpace} ${horizontalSpace} ${verticalSpace} 0`, img);
    } else if (align === 'right') {
        writer.setStyle('float', 'right', img);
        writer.setStyle('margin', `${verticalSpace} 0 ${verticalSpace} ${horizontalSpace}`, img);
    } else if (align === 'center') {
        writer.setStyle('display', 'block', img);
        writer.setStyle('margin', `${verticalSpace} auto`, img);
    } else if (hspace || vspace) {
        writer.setStyle('margin', `${verticalSpace} ${horizontalSpace}`, img);
    }
}

class DisableEmbeddedImageUploadPlugin {
    static get pluginName() {
        return 'DisableEmbeddedImageUpload';
    }

    constructor(editor) {
        this.editor = editor;
    }

    init() {
        const editor = this.editor;

        editor.editing.view.document.on('clipboardInput', (evt, data) => {
            const files = Array.from(data.dataTransfer.files || []);
            if (files.some((file) => file.type && file.type.startsWith('image/'))) {
                evt.stop();
            }
        }, { priority: 'high' });

        editor.editing.view.document.on('dragover', (evt, data) => {
            const files = Array.from(data.dataTransfer.files || []);
            if (files.some((file) => file.type && file.type.startsWith('image/'))) {
                data.dataTransfer.dropEffect = 'none';
            }
        }, { priority: 'high' });

        editor.editing.view.document.on('drop', (evt, data) => {
            const files = Array.from(data.dataTransfer.files || []);
            if (files.some((file) => file.type && file.type.startsWith('image/'))) {
                evt.stop();
                data.preventDefault();
            }
        }, { priority: 'high' });

        editor.model.document.on('change', () => {
            const differ = editor.model.document.differ;

            for (const entry of differ.getChanges()) {
                if (entry.type !== 'insert') {
                    continue;
                }

                if (entry.name !== 'imageBlock' && entry.name !== 'imageInline') {
                    continue;
                }

                const image = entry.position.nodeAfter;
                const src = image && image.getAttribute('src');

                if (src && isEmbeddedImageSrc(src)) {
                    editor.model.enqueueChange({ isUndoable: true }, (writer) => {
                        writer.remove(image);
                    });
                }
            }
        });
    }
}

function isEmbeddedImageSrc(src) {
    return typeof src === 'string' && src.trim().toLowerCase().startsWith('data:');
}

class ImagePropertiesEditing {
    static get pluginName() {
        return 'ImagePropertiesEditing';
    }

    constructor(editor) {
        this.editor = editor;
    }

    init() {
        const editor = this.editor;
        const imageUtils = editor.plugins.get('ImageUtils');

        ['imageBlock', 'imageInline'].forEach((imageType) => {
            if (editor.model.schema.isRegistered(imageType)) {
                editor.model.schema.extend(imageType, { allowAttributes: HTML_IMAGE_ATTRS });
            }
        });

        HTML_IMAGE_ATTRS.forEach((attr) => {
            editor.conversion.for('upcast').attributeToAttribute({
                view: { name: 'img', key: attr },
                model: attr
            });
        });

        const downcastSizeAndHtmlAttrs = (dispatcher, imageType, pipeline) => {
            ['width', 'height', ...HTML_IMAGE_ATTRS].forEach((attr) => {
                dispatcher.on(`attribute:${attr}:${imageType}`, (evt, data, conversionApi) => {
                    if (!conversionApi.consumable.test(data.item, evt.name)) {
                        return;
                    }
                    conversionApi.consumable.consume(data.item, evt.name);

                    const viewElement = conversionApi.mapper.toViewElement(data.item);
                    if (!viewElement) {
                        return;
                    }

                    const img = imageUtils.findViewImgElement(viewElement) || viewElement;
                    const value = data.attributeNewValue;

                    if (attr === 'width' || attr === 'height') {
                        if (value) {
                            conversionApi.writer.setAttribute(attr, value, img);
                            if (pipeline === 'editing' && imageType === 'imageBlock' && attr === 'width') {
                                conversionApi.writer.setStyle('width', toPx(value), viewElement);
                                conversionApi.writer.addClass('image_resized', viewElement);
                            }
                        } else {
                            conversionApi.writer.removeAttribute(attr, img);
                            if (pipeline === 'editing' && imageType === 'imageBlock' && attr === 'width') {
                                conversionApi.writer.removeStyle('width', viewElement);
                                conversionApi.writer.removeClass('image_resized', viewElement);
                            }
                        }
                        return;
                    }

                    if (value) {
                        conversionApi.writer.setAttribute(attr, value, img);
                    } else {
                        conversionApi.writer.removeAttribute(attr, img);
                    }

                    if (attr === 'border') {
                        if (value) {
                            conversionApi.writer.setStyle('border', `${value}px solid`, img);
                        } else {
                            conversionApi.writer.removeStyle('border', img);
                        }
                    }

                    if (pipeline === 'data' && (attr === 'align' || attr === 'hspace' || attr === 'vspace')) {
                        applyImageAlignmentStyles(
                            conversionApi.writer,
                            img,
                            data.item.getAttribute('align') || 'left',
                            data.item.getAttribute('hspace'),
                            data.item.getAttribute('vspace')
                        );
                    }
                }, { priority: 'low' });
            });
        };

        ['imageBlock', 'imageInline'].forEach((imageType) => {
            editor.conversion.for('editingDowncast').add((dispatcher) => {
                downcastSizeAndHtmlAttrs(dispatcher, imageType, 'editing');
            });
            editor.conversion.for('dataDowncast').add((dispatcher) => {
                downcastSizeAndHtmlAttrs(dispatcher, imageType, 'data');
            });
        });

        editor.model.document.on('change', () => {
            const differ = editor.model.document.differ;

            for (const entry of differ.getChanges()) {
                if (entry.type === 'insert' && (entry.name === 'imageBlock' || entry.name === 'imageInline')) {
                    const image = entry.position.nodeAfter;
                    if (!image || image.getAttribute('imageStyle')) {
                        continue;
                    }

                    editor.model.enqueueChange({ isUndoable: true }, (writer) => {
                        writer.setAttribute('imageStyle', alignToImageStyle(image, 'left'), image);
                        writer.setAttribute('align', 'left', image);
                    });
                }

                if (entry.type === 'attribute' && entry.attributeKey === 'imageStyle') {
                    let image = null;

                    for (const item of entry.range.getItems()) {
                        if (imageUtils.isImage(item)) {
                            image = item;
                            break;
                        }
                    }

                    if (!image && imageUtils.isImage(entry.range.start.parent)) {
                        image = entry.range.start.parent;
                    }

                    if (!image) {
                        continue;
                    }

                    editor.model.enqueueChange({ isUndoable: true }, (writer) => {
                        writer.setAttribute('align', imageStyleToAlign(entry.attributeNewValue), image);
                    });
                }
            }
        });
    }
}

class ImagePropertiesUI {
    static get pluginName() {
        return 'ImagePropertiesUI';
    }

    constructor(editor) {
        this.editor = editor;
    }

    init() {
        const editor = this.editor;

        editor.ui.componentFactory.add('imageProperties', (locale) => {
            const ButtonView = editor.ui.componentFactory.create('bold').constructor;
            const button = new ButtonView(locale);

            button.set({
                label: 'Image',
                icon: IMAGE_ICON,
                tooltip: 'Image Properties'
            });

            button.on('execute', () => {
                editor.fire('imageProperties:open');
            });

            return button;
        });

        editor.editing.view.document.on('dblclick', (evt, data) => {
            let target = data.target;
            while (target) {
                const isImage = target.is && (
                    target.is('element', 'img') ||
                    (target.is('element', 'figure') && target.hasClass('image'))
                );
                if (isImage) {
                    editor.fire('imageProperties:open');
                    evt.stop();
                    data.preventDefault();
                    return;
                }
                target = target.parent;
            }
        });
    }
}

function getSelectedImage(editor) {
    if (!editor) {
        return null;
    }
    return editor.plugins.get('ImageUtils').getClosestSelectedImageElement(editor.model.document.selection);
}

function readImageValues(image) {
    if (!image) {
        return {
            url: '',
            alt: '',
            width: '',
            height: '',
            border: '',
            hspace: '',
            vspace: '',
            align: ''
        };
    }

    return {
        url: image.getAttribute('src') || '',
        alt: image.getAttribute('alt') || '',
        width: image.getAttribute('width') ? String(image.getAttribute('width')) : '',
        height: image.getAttribute('height') ? String(image.getAttribute('height')) : '',
        border: image.getAttribute('border') || '',
        hspace: image.getAttribute('hspace') || '',
        vspace: image.getAttribute('vspace') || '',
        align: image.getAttribute('align') || imageStyleToAlign(image.getAttribute('imageStyle'))
    };
}

function applyImageValues(editor, values) {
    const imageUrl = (values.url || '').trim();

    if (!imageUrl || isEmbeddedImageSrc(imageUrl)) {
        window.alert('Please enter a valid image URL. Embedded/base64 images are not allowed.');
        return false;
    }

    const imageUtils = editor.plugins.get('ImageUtils');
    let image = getSelectedImage(editor);

    editor.model.change((writer) => {
        if (!image) {
            image = imageUtils.insertImage(
                { src: imageUrl, alt: values.alt || '' },
                null,
                null,
                { setImageSizes: false }
            );
        }

        if (!image) {
            return;
        }

        const resolvedAlign = values.align || (values.width || values.height ? 'left' : '');
        const attrs = {
            src: imageUrl,
            alt: values.alt || null,
            width: values.width || null,
            height: values.height || null,
            border: values.border || null,
            hspace: values.hspace || null,
            vspace: values.vspace || null,
            align: resolvedAlign || null,
            imageStyle: resolvedAlign ? alignToImageStyle(image, resolvedAlign) : null
        };

        Object.keys(attrs).forEach((name) => {
            if (attrs[name] === null || attrs[name] === '') {
                writer.removeAttribute(name, image);
            } else {
                writer.setAttribute(name, attrs[name], image);
            }
        });
    });

    return true;
}

const DEFAULT_CONFIG = {
    removePlugins: [
        'EasyImage',
        'CKBox',
        'CKFinder',
        'ImageUpload'
    ],
    extraPlugins: [
        DisableEmbeddedImageUploadPlugin,
        ImagePropertiesEditing,
        ImagePropertiesUI,
        CustomStylesEditing,
        CustomStylesUI
    ],
    toolbar: {
        items: [
            'undo', 'redo', '|',
            'heading', 'customStyles', '|',
            'bold', 'italic', '|',
            'link', 'imageProperties', 'insertTable', 'blockQuote', 'mediaEmbed', '|',
            'bulletedList', 'numberedList', 'outdent', 'indent'
        ],
        shouldNotGroupWhenFull: true
    },
    image: {
        toolbar: [
            'imageStyle:wrapText',
            'imageStyle:breakText',
            '|',
            'imageProperties'
        ]
    },
    table: {
        contentToolbar: [
            'tableColumn',
            'tableRow',
            'mergeTableCells'
        ]
    }
};

const CKEditors = (props) => {
    const editorRef = useRef(null);
    const [showProperties, setShowProperties] = useState(false);
    const [imageValues, setImageValues] = useState(null);
    const editorData = props.content != null ? props.content : (props.data || '');
    const className = props.activeclassName || props.activeClass || '';
    const events = props.events || {};

    const openProperties = useCallback(() => {
        const editor = editorRef.current;
        if (!editor) {
            return;
        }
        setImageValues(readImageValues(getSelectedImage(editor)));
        setShowProperties(true);
    }, []);

    const closeProperties = useCallback(() => {
        setShowProperties(false);
        setImageValues(null);
    }, []);

    const saveProperties = useCallback((values) => {
        const editor = editorRef.current;
        if (editor && applyImageValues(editor, values)) {
            if (events.change) {
                events.change({ editor });
            }
            closeProperties();
        }
    }, [closeProperties, events]);

    return (
        <div className={className}>
            <CKEditor
                editor={ClassicEditor}
                data={editorData}
                config={{ ...DEFAULT_CONFIG, ...(props.config || {}) }}
                onReady={(editor) => {
                    editorRef.current = editor;
                    editor.on('imageProperties:open', openProperties);
                }}
                onChange={(event, editor) => {
                    if (events.change) {
                        events.change({ editor, event });
                    }
                }}
            />
            <ImagePropertiesModal
                isOpen={showProperties}
                initialValues={imageValues}
                onCancel={closeProperties}
                onSave={saveProperties}
            />
        </div>
    );
};

export default CKEditors;
